import { DialogActions, DialogContent } from '@material-ui/core';
import { TreeItem as MuiTreeItem } from '@material-ui/lab';
import { TreeItemProps as MuiTreeItemProps } from '@material-ui/lab/TreeItem/TreeItem';
import { find } from 'lodash-es';
import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import folderIcon from '../../assets/img/folder.svg';
import galleryIcon from '../../assets/img/gallery.svg';
import { usePathFromHierarchy } from '../../hooks/usePathFromHierarchy';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { DialogTitle } from '../common/Dialog';
import { TreeItemLabel, TreeView, useFolderTreeStyles } from '../common/FolderTree';
import { CloseIconButton } from '../common/IconButton';
import { Loader } from '../common/Loader';
import { TextFieldBoxed } from '../common/TextFieldBoxed';
import { Typography } from '../common/Typography';
import {
  getAllGalleries,
  getFolderHierarchy,
  useGetFolderHierarchy,
} from '../services/UploadManagementService';
import {
  Content,
  Folder,
  FolderHierarchy,
  ROOT_FOLDER_ID,
  RootFolder,
} from '../services/UploadManagementServiceTypes';
import { GalleryActions } from './SaveGalleryModals';

export interface CreateGalleryModalProps {
  onAction: (options: GalleryActions) => Promise<void>;
  onContinue: () => void;
  onClose: () => void;
  currentFolder: Folder | RootFolder;
}

interface CreateGalleryModalState {
  galleryName: string;
  destinationGuid: string | null;
}

export const CreateGalleryModal: React.FC<CreateGalleryModalProps> = ({
  onAction,
  onClose,
  currentFolder,
}) => {
  const [{ galleryName, destinationGuid }, setState] = useState<CreateGalleryModalState>({
    galleryName: 'New Gallery',
    destinationGuid: currentFolder.guid,
  });
  const { data: galleries, isLoading: loadingGalleries } = useQuery(
    ['fetchGalleries'],
    getAllGalleries,
  );
  const {
    data: folderHierarchy,
    isLoading: loadingHierarchy,
    isError: errorHierarchy,
  } = useGetFolderHierarchy(getFolderHierarchy);
  const { pathToContent } = usePathFromHierarchy({ folderHierarchy, content: currentFolder });
  const { mutate, isError, isLoading: loadingSubmit } = useMutation(onAction);
  const loading = loadingSubmit || loadingHierarchy || loadingGalleries;

  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const galleryName = event.target.value;
    setState((state) => ({ ...state, galleryName }));
  };

  const handleOnNodeSelect = (_event: any, nodeId: string) => {
    const clickedGallery = find(galleries, { guid: nodeId });
    if (clickedGallery) {
      setState({
        galleryName: clickedGallery.name || galleryName,
        destinationGuid: clickedGallery.parentGuid ?? null,
      });
    } else {
      setState({ galleryName: galleryName, destinationGuid: nodeId });
    }
  };

  const handleCreate = (event: FormEvent) => {
    event.preventDefault();
    const duplicate = find(galleries, { parentGuid: destinationGuid, name: galleryName });
    if (duplicate) {
      mutate({ galleryName, destinationGuid, duplicateId: duplicate.guid, action: 'update' });
    } else {
      mutate({ galleryName, destinationGuid, action: 'create' });
    }
  };
  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    input.select();
  }, [inputRef]);

  return (
    <>
      <form onSubmit={handleCreate}>
        <DialogTitle>Save Panorama Gallery</DialogTitle>
        <DialogContent>
          <Typography>Name your panorama gallery</Typography>
          <TextFieldBoxed
            inputRef={inputRef}
            value={galleryName}
            onChange={handleInputOnChange}
            autoFocus={true}
            required={true}
            InputLabelProps={{ required: false }}
            id={'create-gallery-textfield'}
            label={'Name'}
            inputProps={{ maxLength: 50, min: 1 }}
          />
          <Typography paragraph>Select a folder for your panorama gallery.</Typography>
          {(loadingHierarchy || loadingGalleries) && <Loader />}
          {folderHierarchy && galleries && (
            <TreeView
              onNodeSelect={handleOnNodeSelect}
              expanded={pathToContent}
              selected={destinationGuid || ROOT_FOLDER_ID}
            >
              <FileTreeNode node={folderHierarchy} galleries={galleries} />
            </TreeView>
          )}
          {isError && (
            <Alert
              severity={'error'}
              shown={isError}
              text='Your gallery could not be saved, please try again later.'
            />
          )}
          {errorHierarchy && (
            <Alert
              severity={'error'}
              shown={errorHierarchy}
              text='Your data could not be loaded.'
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            type='submit'
            disabled={loading || !destinationGuid || !galleryName}
            aria-label='Save'
          >
            Save
          </Button>
          <CloseIconButton onClick={onClose} disabled={loadingSubmit} />
        </DialogActions>
      </form>
    </>
  );
};

const FileTreeNode: React.FC<{ node: FolderHierarchy; galleries: Content[] }> = ({
  node,
  galleries,
}) => {
  const galleriesInFolder = galleries.filter((gallery) => gallery.parentGuid === node.guid);

  return (
    <TreeItem nodeId={node.guid} labelName={node.name}>
      {galleriesInFolder.map((gallery, index) => (
        <TreeItem key={index} nodeId={gallery.guid} labelName={gallery.name} isGallery />
      ))}
      {node.subHierarchy.map((child, index) => (
        <FileTreeNode key={index} node={child} galleries={galleries} />
      ))}
    </TreeItem>
  );
};

interface TreeItemProps extends MuiTreeItemProps {
  isGallery?: boolean;
  labelName?: string;
}

const TreeItem: React.FC<TreeItemProps> = ({ isGallery, nodeId, labelName = '', children }) => {
  const classes = useFolderTreeStyles();

  const icon = isGallery ? galleryIcon : folderIcon;
  return (
    <MuiTreeItem
      classes={{
        root: classes.treeItemRoot,
        label: `${classes.treeItemBase} ${classes.treeItemDefault}`,
      }}
      nodeId={nodeId}
      label={<TreeItemLabel name={labelName} src={icon} />}
    >
      {children}
    </MuiTreeItem>
  );
};
